import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import CountBox from "@/components/ui/CountBox";
import DateBox from "@/components/ui/DateBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import SelectBox from "@/components/ui/SelectBox";
import MultiSelectBox from "@/components/ui/MultiSelectBox";
import SalesChart from "@/components/charts/SalesChart";
import SalesRankChart from "@/components/charts/SalesRankChart";
import {
  transformChartData,
  generateCompanyConfig,
  calculateDomain,
  generateYAxisTicks,
  type RawChartData,
} from "@/lib/chartUtils";
import { useState } from "react";
import { useCodeName, useSaleRank, useSaleTopList, useSaleTotal } from "@/hooks/performance/useSaleSlip";
import SalesTotalChart from "@/components/charts/SalesTotalChart";

export default function SalesAnalysis() {
  // 올해 1월 1일과 오늘 날짜 계산
  const currentYear = new Date().getFullYear();
  const currentDate = new Date();
  const defaultStartDate = `${currentYear}-01-01`;
  const defaultEndDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;

  const [selectedCompanyCodes, setSelectedCompanyCodes] = useState<string[]>([]);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [rangeBtn, setRangeBtn] = useState("today");
  const [topCompany, setTopCompany] = useState("5");
  const { data: codeName } = useCodeName(
    { dateFrom: startDate, dateTo: endDate },
    { enabled: !!startDate && !!endDate }
  );
  const companyOptions = codeName?.map(item => ({ label: item.stCompanyName, value: item.stCompanyCode })) || [];

  const topCompanyOptions = [
    { label: "5", value: "5", default: true },
    { label: "10", value: "10" },
    { label: "15", value: "15" },
  ];

  // 왼쪽 차트 데이터 (topCompany 값이 바뀔 때마다 자동 호출)
  const { data: saleTopList, isLoading: isLoadingTopList } = useSaleTopList({ topN: Number(topCompany) || 5 });
  // 왼쪽 아래 차트 데이터
  const { data: saleYtdSummary } = useSaleTotal();

  const totalSales = saleYtdSummary?.totalSales;
  const totalCompanies = saleYtdSummary?.totalCompanies;
  const growthRate = saleYtdSummary?.yoyGrowthRate;

  // API에서 받아온 데이터를 차트용으로 변환
  const rawData: RawChartData[] = saleTopList || [];
  const chartData = transformChartData(rawData);
  const companies = generateCompanyConfig(rawData);
  const [yMin, yMax] = calculateDomain(rawData);
  const yAxisTicks = generateYAxisTicks(yMin, yMax);

  const handleDateSelect = (range: { startDate: string; endDate: string }) => {
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const handleReset = () => {
    setSelectedCompanyCodes([]);
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setRangeBtn("today");
    setTopCompany("5");
  };

  const handleSearch = () => {
    // 조회 로직이 필요한 경우 여기에 추가
    console.log("조회:", { topCompany });
  };

  // 오른쪽 차트 데이터 (순위 차트)
  const { data: saleRank } = useSaleRank({ dateFrom: startDate, dateTo: endDate, companyCodes: selectedCompanyCodes });

  return (
    <div className="flex flex-col h-full bg-white">
      <SearchBoxLayout>
        <div className="flex items-end">
          <DateBox
            label="조회기간"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-3" />
          <DateSearchBtn
            mode="range"
            value={rangeBtn}
            onChangeValue={setRangeBtn}
            currentRange={{ startDate, endDate }}
            onDateSelect={handleDateSelect}
          />
          <div className="mr-3" />
          <MultiSelectBox
            label="회사"
            className="w-50"
            values={selectedCompanyCodes}
            onChange={setSelectedCompanyCodes}
            options={companyOptions}
          />
          <div className="mr-3" />
          <SelectBox
            label="상위업체"
            className="w-25"
            value={topCompany || ""}
            onChange={setTopCompany}
            options={topCompanyOptions}
          />

          <div className="flex gap-[6px] ml-auto">
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex justify-end gap-2 mb-2">
        </div>

        <div className="flex-1 flex gap-4 pb-4">
          <div className="flex-[1] flex flex-col bg-white rounded-lg border border-gray-200">
            <div className="bg-[#D0E7AC] border border-[#01012E14] rounded-tl-lg rounded-tr-lg">
              <p className="text-[#333333] text-base font-semibold pl-6 py-6">TOP 업체별 매출변동 추이</p>
            </div>
            {/* 차트 영역 */}
            <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: "300px" }}>
              {isLoadingTopList ? (
                <div className="flex items-center justify-center h-full text-gray-500">로딩 중...</div>
              ) : chartData.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500">데이터가 없습니다.</div>
              ) : (
                <SalesChart data={chartData} companies={companies} yDomain={[yMin, yMax]} yTicks={yAxisTicks} />
              )}
            </div>
            {/* 메트릭 카드 영역 */}
            <div className="pb-5 px-4 flex justify-center items-center">
              <SalesTotalChart totalSales={totalSales} totalCompanies={totalCompanies} growthRate={growthRate} />
            </div>
          </div>
          <div className="flex-[1] flex flex-col bg-white rounded-lg border border-gray-200 ">
            <div className="bg-[#D0E7AC] border border-[#01012E14] rounded-tl-lg rounded-tr-lg">
              <p className="text-[#333333] text-base font-semibold pl-6 py-6">선택 월 업체별 매출 순위</p>
            </div>
            {/* 차트 영역 */}
            <div className="flex-1 overflow-hidden">
              <SalesRankChart data={saleRank || []} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
